import React, { createContext, useEffect, useRef, useCallback } from 'react';
import { createSseTicket } from '../services/api';

export const RideEventsContext = createContext(null);

export function RideEventsProvider({ user, children }) {
  const listenersRef = useRef(new Set());
  const vehicleLocationListenersRef = useRef(new Set());
  const vehicleOfflineListenersRef = useRef(new Set());

  useEffect(() => {
    if (!user) return;

    let es = null;
    let cancelled = false;

    async function connect() {
      const token = localStorage.getItem('taxi-token');
      if (!token || cancelled) return;
      try {
        const res = await createSseTicket();
        if (cancelled) return;
        const base = process.env.REACT_APP_API_URL || 'http://localhost:8080';
        es = new EventSource(`${base}/rides/events?ticket=${encodeURIComponent(res.data.ticket)}`);
        es.addEventListener('refresh', () => {
          listenersRef.current.forEach((fn) => fn());
        });
        es.addEventListener('vehicle-location', (e) => {
          try {
            const data = JSON.parse(e.data);
            vehicleLocationListenersRef.current.forEach((fn) => fn(data));
          } catch { /* ignore malformed payload */ }
        });
        es.addEventListener('vehicle-offline', (e) => {
          try {
            const data = JSON.parse(e.data);
            vehicleOfflineListenersRef.current.forEach((fn) => fn(data));
          } catch { /* ignore malformed payload */ }
        });
        es.onerror = () => {
          es.close();
          if (!cancelled) setTimeout(connect, 3000);
        };
      } catch {
        if (!cancelled) setTimeout(connect, 5000);
      }
    }

    connect();

    return () => {
      cancelled = true;
      if (es) es.close();
    };
  }, [user]);

  const subscribe = useCallback((fn) => {
    listenersRef.current.add(fn);
    return () => listenersRef.current.delete(fn);
  }, []);

  const subscribeVehicleLocation = useCallback((fn) => {
    vehicleLocationListenersRef.current.add(fn);
    return () => vehicleLocationListenersRef.current.delete(fn);
  }, []);

  const subscribeVehicleOffline = useCallback((fn) => {
    vehicleOfflineListenersRef.current.add(fn);
    return () => vehicleOfflineListenersRef.current.delete(fn);
  }, []);

  const value = { subscribe, subscribeVehicleLocation, subscribeVehicleOffline };

  return <RideEventsContext.Provider value={value}>{children}</RideEventsContext.Provider>;
}
